import { round, isNumber } from 'lodash'

export function NumberConversion({
  numDigits = 4,
  scalingFactor = 1,
  asAbs = false,
  maxFractionalPortion = null
} = {}) {
  numDigits = numDigits || 4
  scalingFactor = scalingFactor || 1

  /**
   * @param {numeric} number
   * @param {string} unit
   *
   * @return {array} [converted-number, converted-unit]
   */
  this.convert = function (number, unit) {
    if (number === undefined) {
      return [number, unit]
    } else if (unit === undefined || unit === null) {
      return [(scalingFactor * number).toFixed(numDigits), '']
    }

    number = asAbs ? Math.abs(number) : number

    number = parseFloat(number)
    if (isNaN(number)) {
      console.warn('The `number` Parameter has to be a number.')
    }

    number = scalingFactor * number
    const initialN = number
    const initialU = unit

    let sgn = 1
    if (number < 0) {
      sgn = -1
      number *= -1
    }

    const sizeDif = sizeDifference(number)
    number /= Math.pow(10, 3 * sizeDif)
    number = roundToDigits(number)

    switch (unit[0]) {
      case 'C':
        return evalConvert(sizeDif, number, '°C')
      case 'k':
        return evalConvert(sizeDif + 1, number, unit.substring(1))
      case 'M':
        return evalConvert(sizeDif + 2, number, unit.substring(1))
      case 'G':
        return evalConvert(sizeDif + 3, number, unit.substring(1))
      case 'l':
        switch (sizeDif) {
          case -1:
            return [roundToDigits(initialN), initialU]
          case 0:
            return [sgn * number, unit]
          case 1:
            // l -> m^3 iff >= 10 000 l
            if (number < 10) {
              unit = initialU
              number = roundToDigits(initialN, numDigits - 1)
            } else {
              unit = initialU.replace(/^l/, 'm^3')
              number = sgn * number
            }
            return [number, unit]
          default:
            return [roundToDigits(initialN), initialU]
        }
      default:
        return evalConvert(sizeDif, number, unit)
    }

    // internal
    function evalConvert(s, n, u) {
      if (s <= -1) {
        return [roundToDigits(initialN), initialU]
      }

      switch (s) {
        case 0:
          return [sgn * n, u]
        case 1:
          u = 'k' + u
          return [sgn * n, u]
        case 2:
          u = 'M' + u
          return [sgn * n, u]
        case 3:
          u = 'G' + u
          return [sgn * n, u]
        default:
          u = 'G' + u
          n = initialN
          switch (initialU[0]) {
            case 'G':
              n /= Math.pow(10, 0)
              break
            case 'M':
              n /= Math.pow(10, 3)
              break
            case 'k':
              n /= Math.pow(10, 6)
              break
            default:
              n /= Math.pow(10, 9)
          }
          return [roundToDigits(n), u]
      }
    }
  }

  // private
  function sizeDifference(n) {
    let sizeDif = 0
    if (n === 0) {
      return sizeDif
    } else if (n < 1) {
      while (n < 1) {
        n *= 1000
        sizeDif -= 1
      }
    } else {
      while (n > 1000) {
        n /= 1000
        sizeDif += 1
      }
    }

    return sizeDif
  }

  function roundToDigits(n, nDigits) {
    nDigits = nDigits || numDigits
    let toF
    if (n / 100 > 1) {
      toF = nDigits - 3
    } else if (n / 10 > 1) {
      toF = nDigits - 2
    } else {
      toF = nDigits - 1
    }

    if (maxFractionalPortion) {
      toF = toF > maxFractionalPortion ? maxFractionalPortion : toF
    }

    const fixedNum = n.toFixed(toF < 0 ? 0 : toF)
    return parseFloat(fixedNum) === 0 ? '0' : fixedNum
  }
}

/**
 * Typically, the BE uses SI-units, however,
 * sometimes the FE does NOT want to use those,
 * and likes scaled measurements.
 *
 * This function implements the "FE convention"
 *
 * @function
 *
 * @param {string} unit
 *
 * @return {object} with `{ scale, unit }`
 */
export function getScaledUnit(unit) {
  switch (unit) {
    case 'Wh':
      return {
        scale: 0.001,
        unit: 'kWh'
      }
    case 'm3':
    case 'm^3':
      return {
        scale: 1,
        unit: 'm³'
      }
    case 'l':
      return {
        scale: 0.001,
        unit: 'm³'
      }
    default:
      return {
        scale: 1,
        unit: unit ?? 'Wh'
      }
  }
}

/**
 *
 * @function
 *
 * @param {Number} number
 * @param {object} opts
 * @param {integer} opts.scale
 * @param {integer} opts.roundPrecision
 *
 * @return {Number} scaled number
 */
export function scaleNumber(number, { scale = 1, roundPrecision = 0, locale = null } = {}) {
  if (!isNumber(number) || isNaN(number)) {
    return number
  }

  if (locale) return (scale * number).toLocaleString(locale, { maximumFractionDigits: roundPrecision })

  return round(scale * number, roundPrecision)
}
